import { useMutation } from '@tanstack/react-query'
import { queryClient } from 'App'
import useCycleApi from 'common/hooks/useCycleApi'
import useFarmApi from 'common/hooks/useFarmApi'
import usePondApi from 'common/hooks/usePondApi'
import { MessageResponse } from 'common/types/api'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'

import { buildUrlWithParams } from 'utils/url'

import {
  editMarkets,
  createMarkets,
  POND_REQUEST_CONFIG,
  CYCLE_REQUEST_CONFIG,
  EXPORT_REQUEST_CONFIG,
  editFeedPrograms,
  createFeedPrograms,
  putDashboardMedicine,
  putDashboardMeal,
  patchDashboardRecord,
  putDashboardFoodNote,
  putDashboardShrimpHealth,
  putDashboardWaterTreatment,
  putDashboardDailyNote,
  putDashboardWaterVibrio,
  createPondMedia,
  deletePondMedia,
  updateTagPondMedia,
  DASHBOARD_REQUEST_CONFIG,
  DASHBOARD_REQUEST_CONFIG_LAB_TABLE,
} from '.'
import { selectedCycleIdState } from '../stores/atom'
import {
  useDiseaseDashboardLabTablePagination,
  useGetCycleById,
  useGetCycles,
  useGetFeedPrograms,
  useGetFeedProgramsById,
  useGetMarkets,
  useGetMarketsById,
  useGetPonds,
  useGetPondInfo,
  useGetRevampPondDashboardPagination,
} from './queries'
import { useDiseaseLabTableSelector, usePondInfoById } from './selector'
import {
  CreateCycleVariables,
  CreatePondVariables,
  EditCycleVariables,
  EditPondVariables,
  ExportDailyParams,
  ExportFeaturesParams,
  LabCellPatch,
  MedicineCategory,
  ProgressCb,
  SortPondItem,
} from './types'

export const useMutateCreatePond = () => {
  const farmApi = useFarmApi()
  const { refetch } = useGetPonds()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(
    ({ body: data }: CreatePondVariables) =>
      farmApi.request<MessageResponse>({
        ...POND_REQUEST_CONFIG.createPond,
        data,
      }),
    {
      onSuccess: () => {
        enqueueSnackbar(t('common:create.success'), { variant: 'success' })
        refetch()
      },
    }
  )
}

export const useMutateRemovePond = () => {
  const farmApi = useFarmApi()
  const { refetch } = useGetPonds()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(
    (pondId: string) =>
      farmApi.request<MessageResponse>({
        ...POND_REQUEST_CONFIG.removePond,
        url: `${POND_REQUEST_CONFIG.removePond.url}/${pondId}`,
      }),
    {
      onSuccess: (res) => {
        const { status } = res.data
        if (status !== 200) return

        enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
        refetch()
      },
    }
  )
}

export const useMutateEditPond = () => {
  const { refetch } = useGetPonds()
  const farmApi = useFarmApi()

  return useMutation(
    ({ pondId, body: data }: EditPondVariables) =>
      farmApi.request<MessageResponse>({
        ...POND_REQUEST_CONFIG.editPond,
        url: `/ponds/${pondId}`,
        data,
      }),
    {
      onSuccess: (res) => {
        const { status } = res.data
        if (status !== 200) return

        refetch()
      },
    }
  )
}

export const useMutateCreateCycle = () => {
  const pondApi = usePondApi()
  const { enqueueSnackbar } = useSnackbar()
  const { refetch } = useGetCycles()
  const { refetch: refetchCycle } = useGetCycleById()
  const { refetch: refetchPonds } = useGetPonds()
  const setCycleId = useSetRecoilState(selectedCycleIdState)
  const { t } = useTranslation(['pond'])

  return useMutation(
    ({ body: data }: CreateCycleVariables) =>
      pondApi.request({
        ...CYCLE_REQUEST_CONFIG.createCycle,
        data,
      }),
    {
      onSuccess: (res) => {
        const { status, data } = res
        if (status !== 200) return

        enqueueSnackbar(t('pond:new.cycle.success'), { variant: 'success' })
        setCycleId(data.data.id)
        refetch()
        refetchCycle()
        refetchPonds()
      },
    }
  )
}

export const useMutateEditCycle = () => {
  const cycleApi = useCycleApi()
  const { refetch: refetchCycle } = useGetCycleById()
  const { refetch: refetchCycles } = useGetCycles()
  const { refetch: refetchPonds } = useGetPonds()
  const { refetch: refetchPondInfo } = useGetPondInfo()

  return useMutation(
    ({ body: data }: EditCycleVariables) =>
      cycleApi.request<MessageResponse>({ ...CYCLE_REQUEST_CONFIG.editCycle, data }),
    {
      onSuccess: (res) => {
        const { status } = res
        if (status !== 200) return

        refetchCycles()
        refetchCycle()
        refetchPonds()
        refetchPondInfo()
      },
    }
  )
}

export const useMutateEndCycle = () => {
  const cycleApi = useCycleApi()
  const { enqueueSnackbar } = useSnackbar()
  const { refetch: refetchCycle } = useGetCycleById()
  const { refetch: refetchCycles } = useGetCycles()
  const { refetch: refetchPonds } = useGetPonds()
  const { t } = useTranslation(['pond'])

  return useMutation(
    (endDate: string) =>
      cycleApi.request<MessageResponse>({
        ...CYCLE_REQUEST_CONFIG.completeCycle,
        data: { endDate },
      }),
    {
      onSuccess: (res) => {
        const { status } = res
        if (status !== 200) return

        enqueueSnackbar(t('pond:new.cycle.success'), { variant: 'success' })
        refetchCycles()
        refetchCycle()
        refetchPonds()
      },
    }
  )
}

export const useMutateRemoveCyCle = () => {
  const cycleApi = useCycleApi()
  const { refetch: refetchPonds } = useGetPonds()

  return useMutation(() => cycleApi.request(CYCLE_REQUEST_CONFIG.removeCycle), {
    onSuccess: (res) => {
      const { status } = res
      if (status !== 200) return

      refetchPonds()
    },
  })
}

export const useMutateEditFeedPrograms = (feedProgramId: number) => {
  const { refetch: refetchPrograms } = useGetFeedPrograms()
  const { refetch: refetchProgramsById } = useGetFeedProgramsById(feedProgramId)

  return useMutation(editFeedPrograms, {
    onSuccess: (res) => {
      const { status } = res
      if (status !== 200) return

      refetchPrograms()
      refetchProgramsById()
    },
  })
}

export const useMutateCreateFeedPrograms = () => {
  const { refetch: refetchPrograms } = useGetFeedPrograms()

  return useMutation(createFeedPrograms, {
    onSuccess: (res) => {
      const { status } = res
      if (status !== 200) return

      refetchPrograms()
    },
  })
}

export const useMutateEditMarkets = (marketId: number) => {
  const { pondId } = useParams()
  const pondInfo = usePondInfoById(pondId ?? '')

  const { refetch: refetchMarkets } = useGetMarkets(pondInfo?.species.id)
  const { refetch: refetchMarketsById } = useGetMarketsById(marketId)

  return useMutation(editMarkets, {
    onSuccess: (res) => {
      const { status } = res
      if (status !== 200) return

      refetchMarkets()
      refetchMarketsById()
    },
  })
}

export const useMutateCreateMarkets = () => {
  const { pondId } = useParams()
  const pondInfo = usePondInfoById(pondId ?? '')

  const { refetch: refetchMarkets } = useGetMarkets(pondInfo?.species.id)

  return useMutation(createMarkets, {
    onSuccess: (res) => {
      const { status } = res
      if (status !== 200) return

      refetchMarkets()
    },
  })
}

export const useMutateExportSummary = () => {
  const cycleApi = useCycleApi()

  return useMutation((progressCb: ProgressCb) =>
    cycleApi.request({
      ...EXPORT_REQUEST_CONFIG.exportSummary,
      onDownloadProgress: progressCb,
    })
  )
}

export const useMutateExportDaily = () => {
  const cycleApi = useCycleApi()

  return useMutation(({ query, progressCb }: ExportDailyParams) =>
    cycleApi.request({
      ...EXPORT_REQUEST_CONFIG.exportDaily,
      url: buildUrlWithParams(EXPORT_REQUEST_CONFIG.exportDaily.url ?? '', { ...query }),
      onDownloadProgress: progressCb,
    })
  )
}

export const useMutateExportFeatures = () => {
  const cycleApi = useCycleApi()

  return useMutation(({ query, progressCb }: ExportFeaturesParams) =>
    cycleApi.request({
      ...EXPORT_REQUEST_CONFIG.exportDaily,
      url: buildUrlWithParams(EXPORT_REQUEST_CONFIG.exportFeatures.url ?? '', { ...query }),
      onDownloadProgress: progressCb,
    })
  )
}

export const useMutatePutDashboardMedicine = () => {
  return useMutation(putDashboardMedicine)
}

export const useMutateCreatePondMedia = () => {
  const { pondId } = useParams()
  const { farmId } = useParams()

  return useMutation({
    mutationFn: createPondMedia, //input onUploadProgress by parameter
    onSuccess: (res) => {
      if (res.status === 200) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'pond-media' &&
            query.queryKey[1] === farmId &&
            query.queryKey[2] === pondId,
        })

        queryClient.removeQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'pond-media' &&
            query.queryKey[1] === farmId &&
            query.queryKey[2] === pondId,
        })
      }
    },
    onError: (err) => {
      console.error('❌ Upload ล้มเหลว', err)
    },
  })
}

export const useMutateDeletePondMedia = () => {
  const { pondId } = useParams()
  const { farmId } = useParams()

  return useMutation({
    mutationFn: deletePondMedia,
    onSuccess: async (res) => {
      if (res.status === 200) {
        //clear after success
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'pond-media' &&
            query.queryKey[1] === farmId &&
            query.queryKey[2] === pondId,
        })

        //Optional: clear cache
        queryClient.removeQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'pond-media' &&
            query.queryKey[1] === farmId &&
            query.queryKey[2] === pondId,
        })
      }
    },
    onError: (err) => {
      console.error('❌ Delete fail', err)
    },
  })
}

export const useMutateUpdateTagPondMedia = () => {
  const { pondId } = useParams()
  const { farmId } = useParams()
  return useMutation({
    mutationFn: updateTagPondMedia,
    onSuccess: (res) => {
      if (res.status === 200) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'pond-media' &&
            query.queryKey[1] === farmId &&
            query.queryKey[2] === pondId,
        })

        queryClient.removeQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'pond-media' &&
            query.queryKey[1] === farmId &&
            query.queryKey[2] === pondId,
        })
      }
    },
    onError: (err) => {
      console.error('❌ Update error', err)
    },
  })
}

// NK-305: no refetch here — useCellEditor patches the saved meal and its
// อาหารสะสม into the cached table. Refetching re-requested every loaded page
// after each feed edit, which froze the table once weeks of history were open.
export const useMutatePutDashboardMeal = () => {
  return useMutation(putDashboardMeal)
}

export const useMutatePutDashboardFoodNote = () => {
  return useMutation(putDashboardFoodNote)
}

export const useMutatePutDashboardShrimpHealth = () => {
  return useMutation(putDashboardShrimpHealth)
}

export const useMutatePutDashboardWaterTreatment = () => {
  return useMutation(putDashboardWaterTreatment)
}

export const useMutatePutDashboardDailyNote = () => {
  return useMutation(putDashboardDailyNote)
}

export const useMutatePatchDashboardRecord = () => {
  return useMutation(patchDashboardRecord)
}

export const useMutatePutDashboardWaterVibrio = () => {
  return useMutation(putDashboardWaterVibrio)
}

export const useMutateCreateNewMedicineRow = () => {
  const cycleApi = useCycleApi()
  const { refetch } = useGetRevampPondDashboardPagination()

  return useMutation(
    (category: MedicineCategory) =>
      cycleApi.request({
        ...DASHBOARD_REQUEST_CONFIG.createNewMedicineRow,
        data: { category },
      }),
    {
      onSuccess: (res) => {
        const { status } = res
        if (status !== 200) return
        refetch()
      },
    }
  )
}

export const useMutateRemoveMedicineRow = () => {
  const cycleApi = useCycleApi()
  const { refetch } = useGetRevampPondDashboardPagination()

  return useMutation(
    (category: MedicineCategory) =>
      cycleApi.request({
        ...DASHBOARD_REQUEST_CONFIG.removeMedicineRow,
        url: `${DASHBOARD_REQUEST_CONFIG.removeMedicineRow.url}/${category}`,
        data: { category },
      }),
    {
      onSuccess: (res) => {
        const { status } = res
        if (status !== 200) return
        refetch()
      },
    }
  )
}

export const useEditdiseaseLabTable = () => {
  const farmApi = useFarmApi()

  return useMutation((data: LabCellPatch) =>
    farmApi.request({
      ...DASHBOARD_REQUEST_CONFIG_LAB_TABLE.editTreatmentLabTable,
      data,
    })
  )
}

export const useEditWaterVibrioLabTable = () => {
  const farmApi = useFarmApi()

  return useMutation((data: LabCellPatch) =>
    farmApi.request({
      ...DASHBOARD_REQUEST_CONFIG_LAB_TABLE.editWaterVibrioLabTable,
      data,
    })
  )
}

export const useEditShrimpHealthLabTable = () => {
  const farmApi = useFarmApi()

  return useMutation((data: LabCellPatch) =>
    farmApi.request({
      ...DASHBOARD_REQUEST_CONFIG_LAB_TABLE.editShrimpHealthLabTable,
      data,
    })
  )
}

export const useMutateSortPonds = () => {
  const farmApi = useFarmApi()

  return useMutation(
    (data: SortPondItem[]) =>
      farmApi.request<MessageResponse>({
        ...POND_REQUEST_CONFIG.sortPonds,
        data,
      }),
    {
      onSuccess: (res) => {
        console.log('✅ Sort ponds success:', res.status, res.data)
      },
      onError: (err) => {
        console.error('❌ Sort ponds failed:', err)
      },
    }
  )
}
